import { useEffect, useState } from 'react';
import { cx } from 'components/crm/ui/uiUtils';
import useText from 'hooks/useText';
import {
  talkClock,
  talkDuration,
  talkingSeconds,
} from 'pages/prospection/liveCall';

const TONES = {
  orange: 'bg-tagOrange-bg text-tagOrange-fg',
  green: 'bg-success-bg text-success-text',
  grey: 'bg-surfaceMuted text-body',
};

const TICK_MS = 1000;

// The row of the 1395 table that applies, in its order. The Wildix rows need
// a live call of the sheet; a ringing one no longer shows once confirmed.
const pillOf = ({ liveCall, confirmed, result, now, text }) => {
  if (liveCall) {
    if (liveCall.status === 'ringing' && !confirmed) {
      return { tone: 'orange', label: text('sheet.status.ringing') };
    }
    if (liveCall.status === 'talking') {
      return {
        tone: 'green',
        pulse: true,
        label: text('sheet.status.talking', {
          time: talkClock(talkingSeconds(liveCall, now)),
        }),
      };
    }
    if (liveCall.status === 'completed') {
      return liveCall.answered
        ? {
            tone: 'grey',
            label: text('sheet.status.endedAfter', {
              duration: talkDuration(liveCall.talkSeconds, text),
            }),
          }
        : { tone: 'grey', label: text('sheet.status.notAnswered') };
    }
  }
  if (!result) {
    return { tone: 'green', pulse: true, label: text('sheet.status.inProgress') };
  }
  if (result === 'no_answer') {
    return { tone: 'grey', label: text('sheet.status.notAnswered') };
  }
  return { tone: 'grey', label: text('sheet.status.ended') };
};

// The state of the call in the sheet's header (JBWEB-1395). liveCall: the
// Wildix call of the sheet, or null; result: the result chosen, or null.
const CallStatusPill = ({ liveCall = null, confirmed = false, result = null }) => {
  const { text } = useText('prospection');
  const [now, setNow] = useState(() => new Date());
  const talking = liveCall?.status === 'talking';

  // The clock only runs while on the line.
  useEffect(() => {
    if (!talking) return undefined;
    setNow(new Date());
    const timer = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(timer);
  }, [talking]);

  const pill = pillOf({ liveCall, confirmed, result, now, text });
  return (
    <span
      role="status"
      className={cx(
        'inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold leading-4',
        TONES[pill.tone],
      )}
    >
      {pill.pulse ? (
        <span
          aria-hidden="true"
          className="size-2 animate-pulse rounded-full bg-success-bar"
        />
      ) : null}
      {pill.label}
    </span>
  );
};

export default CallStatusPill;
